import React, { useState, useMemo, useCallback } from 'react';
import { X, Search, ChevronRight, ChevronLeft, Keyboard, Lightbulb, BookOpen, ArrowRight, Play } from 'lucide-react';
import { MANUAL_CATEGORIES, KEYBOARD_SHORTCUTS, GUIDED_TOURS } from './manualContent';

const UserManualModal = ({ isOpen, onClose, onStartTour }) => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('home'); // home | category | shortcuts | tours

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return MANUAL_CATEGORIES;
    const q = searchQuery.toLowerCase();
    return MANUAL_CATEGORIES.filter(cat =>
      cat.title.toLowerCase().includes(q) ||
      cat.description.toLowerCase().includes(q) ||
      cat.sections.some(s =>
        s.title.toLowerCase().includes(q) ||
        s.content.toLowerCase().includes(q)
      )
    );
  }, [searchQuery]);

  const openCategory = useCallback((cat) => {
    setActiveCategory(cat);
    setActiveSection(0);
    setView('category');
  }, []);

  const goHome = useCallback(() => {
    setView('home');
    setActiveCategory(null);
    setActiveSection(0);
  }, []);

  if (!isOpen) return null;

  const renderMarkdown = (text) => {
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-2" />;

      // Bold sections
      let processed = trimmed;
      const parts = [];
      let lastIndex = 0;
      const boldRegex = /\*\*(.*?)\*\*/g;
      let match;
      while ((match = boldRegex.exec(processed)) !== null) {
        if (match.index > lastIndex) {
          parts.push(<span key={`t${i}-${lastIndex}`}>{processed.slice(lastIndex, match.index)}</span>);
        }
        parts.push(<span key={`b${i}-${match.index}`} className="font-semibold text-zinc-100">{match[1]}</span>);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < processed.length) {
        parts.push(<span key={`e${i}`}>{processed.slice(lastIndex)}</span>);
      }

      if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
        return (
          <div key={i} className="flex gap-2 ml-3 mb-1">
            <span className="text-fuchsia-400 mt-0.5 shrink-0">-</span>
            <span>{parts.length > 0 ? parts : trimmed.slice(2)}</span>
          </div>
        );
      }

      return <p key={i} className="mb-1.5">{parts.length > 0 ? parts : trimmed}</p>;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      data-testid="user-manual-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-[90vw] max-w-[1100px] h-[82vh] bg-zinc-900 border border-zinc-700/80 rounded-xl shadow-2xl flex flex-col overflow-hidden"
        data-testid="user-manual-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900/95 shrink-0">
          <div className="flex items-center gap-3">
            {view !== 'home' && (
              <button
                onClick={goHome}
                className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
                data-testid="manual-back-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <BookOpen className="w-5 h-5 text-fuchsia-400" />
            <h2 className="text-base font-semibold text-zinc-100 tracking-tight">
              {view === 'home' && 'User Manual'}
              {view === 'category' && activeCategory?.title}
              {view === 'shortcuts' && 'Keyboard Shortcuts'}
              {view === 'tours' && 'Guided Tours'}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
              <input
                type="text"
                placeholder="Search features..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value && view !== 'home') goHome();
                }}
                className="w-52 h-8 pl-8 pr-3 text-xs bg-zinc-800 border border-zinc-700 rounded-md text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-fuchsia-500/60"
                data-testid="manual-search-input"
              />
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-zinc-800 text-zinc-400 hover:text-white transition-colors"
              data-testid="manual-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden">
          {view === 'home' && (
            <div className="h-full overflow-y-auto p-6">
              {/* Quick Actions */}
              <div className="flex gap-3 mb-6">
                <button
                  onClick={() => setView('shortcuts')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800/80 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300 hover:text-white transition-all"
                  data-testid="manual-shortcuts-btn"
                >
                  <Keyboard className="w-4 h-4 text-amber-400" />
                  Keyboard Shortcuts
                </button>
                <button
                  onClick={() => setView('tours')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-zinc-800/80 hover:bg-zinc-800 border border-zinc-700 rounded-lg text-xs text-zinc-300 hover:text-white transition-all"
                  data-testid="manual-tours-btn"
                >
                  <Play className="w-4 h-4 text-green-400" />
                  Interactive Tours
                </button>
              </div>

              {/* Category Grid */}
              <div className="grid grid-cols-3 gap-3">
                {filteredCategories.map(cat => {
                  const Icon = cat.icon;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => openCategory(cat)}
                      className="group flex flex-col items-start gap-2 p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/60 hover:border-zinc-600 rounded-lg text-left transition-all"
                      data-testid={`manual-cat-${cat.id}`}
                    >
                      <div className="flex items-center gap-2.5 w-full">
                        <div
                          className="w-8 h-8 rounded-md flex items-center justify-center shrink-0"
                          style={{ backgroundColor: cat.color + '18', border: `1px solid ${cat.color}30` }}
                        >
                          <Icon className="w-4 h-4" style={{ color: cat.color }} />
                        </div>
                        <span className="text-sm font-medium text-zinc-200 group-hover:text-white flex-1">{cat.title}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                      </div>
                      <p className="text-[11px] text-zinc-500 leading-relaxed pl-[42px]">{cat.description}</p>
                    </button>
                  );
                })}
              </div>

              {filteredCategories.length === 0 && searchQuery && (
                <div className="text-center py-12 text-zinc-500 text-sm">
                  No results for "{searchQuery}"
                </div>
              )}
            </div>
          )}

          {view === 'category' && activeCategory && (
            <div className="flex h-full">
              {/* Section sidebar */}
              <div className="w-56 shrink-0 border-r border-zinc-800 overflow-y-auto bg-zinc-900/60 py-2">
                {activeCategory.sections.map((section, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveSection(idx)}
                    className={`w-full text-left px-4 py-2.5 text-xs transition-colors ${
                      activeSection === idx
                        ? 'bg-fuchsia-500/10 text-fuchsia-300 border-r-2 border-fuchsia-500'
                        : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
                    }`}
                    data-testid={`manual-section-${idx}`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>

              {/* Section content */}
              <div className="flex-1 overflow-y-auto p-6">
                <h3 className="text-lg font-semibold text-zinc-100 mb-4">
                  {activeCategory.sections[activeSection]?.title}
                </h3>

                <div className="text-xs text-zinc-400 leading-relaxed mb-6">
                  {renderMarkdown(activeCategory.sections[activeSection]?.content || '')}
                </div>

                {/* Tips */}
                {activeCategory.sections[activeSection]?.tips?.length > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-xs font-semibold text-amber-300">Tips</span>
                    </div>
                    {activeCategory.sections[activeSection].tips.map((tip, i) => (
                      <div key={i} className="flex gap-2 text-[11px] text-amber-200/70 mb-1.5 last:mb-0">
                        <ArrowRight className="w-3 h-3 shrink-0 mt-0.5 text-amber-500/60" />
                        <span>{tip}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Nav buttons */}
                <div className="flex justify-between mt-8 pt-4 border-t border-zinc-800">
                  <button
                    disabled={activeSection === 0}
                    onClick={() => setActiveSection(prev => prev - 1)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="manual-prev-btn"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" /> Previous
                  </button>
                  <button
                    disabled={activeSection === activeCategory.sections.length - 1}
                    onClick={() => setActiveSection(prev => prev + 1)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="manual-next-btn"
                  >
                    Next <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {view === 'shortcuts' && (
            <div className="h-full overflow-y-auto p-6">
              <div className="space-y-6">
                {KEYBOARD_SHORTCUTS.map((group, gi) => (
                  <div key={gi}>
                    <h3 className="text-sm font-semibold text-zinc-300 mb-3">{group.category}</h3>
                    <div className="bg-zinc-800/40 rounded-lg border border-zinc-700/50 overflow-hidden">
                      {group.shortcuts.map((sc, si) => (
                        <div
                          key={si}
                          className={`flex items-center justify-between px-4 py-2.5 ${
                            si < group.shortcuts.length - 1 ? 'border-b border-zinc-800' : ''
                          }`}
                        >
                          <span className="text-xs text-zinc-400">{sc.action}</span>
                          <kbd className="px-2.5 py-1 bg-zinc-700/60 border border-zinc-600/60 rounded text-[11px] text-zinc-200 font-mono tracking-wide">
                            {sc.keys}
                          </kbd>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {view === 'tours' && (
            <div className="h-full overflow-y-auto p-6">
              <p className="text-xs text-zinc-500 mb-5">
                Interactive tours highlight UI elements and walk you through features step-by-step.
                The manual will close and the tour will begin in the editor.
              </p>
              <div className="space-y-3">
                {GUIDED_TOURS.map(tour => (
                  <button
                    key={tour.id}
                    onClick={() => {
                      onClose();
                      setTimeout(() => onStartTour?.(tour.id), 300);
                    }}
                    className="w-full flex items-center gap-4 p-4 bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700/60 hover:border-fuchsia-500/30 rounded-lg text-left transition-all group"
                    data-testid={`manual-tour-${tour.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0">
                      <Play className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-zinc-200 group-hover:text-white">{tour.title}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{tour.description}</div>
                    </div>
                    <div className="text-[10px] text-zinc-600 shrink-0">{tour.steps.length} steps</div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-fuchsia-400 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManualModal;
